/**
 * Aceitar um convite — a única tela do Fincast que abre SEM sessão.
 *
 * O convidado chega por um link que o gestor mandou, escolhe uma senha e entra. É o
 * único caminho de entrada no produto: não existe "criar conta".
 *
 * ⚠ O E-MAIL VEM NO LINK E NÃO É EDITÁVEL, e isso não é rigidez à toa. Quem valida o
 * convite é um gatilho no banco, que procura um convite pendente **para aquele e-mail**.
 * Se a pessoa digitasse outro, o gatilho recusaria — e a recusa chega ao navegador como
 * um erro 500 sem corpo, porque o servidor de login não repassa a mensagem do Postgres.
 * Seria um beco sem explicação. Travar o campo faz o caso desaparecer.
 */
import { h } from '../dom';
import { icone } from '../icones';
import { cadastrarComConvite } from '../../../../packages/dados/src/sessao';

export interface DadosConvite {
  token: string;
  email: string;
}

/** Lê `/convite/<token>?e=<email>`. */
export function lerConviteDaUrl(): DadosConvite | null {
  const partes = window.location.pathname.split('/').filter((p) => p !== '');
  if (partes[0] !== 'convite' || partes[1] === undefined) return null;
  const email = new URLSearchParams(window.location.search).get('e') ?? '';
  return { token: decodeURIComponent(partes[1]), email };
}

function moldura(...filhos: (Node | string | null)[]): HTMLElement {
  return h('div', { style: 'min-height:100vh;display:grid;place-items:center;padding:24px' },
    h('div', {
      class: 'card',
      style: 'width:min(430px,94vw);padding:30px;display:grid;gap:16px',
      'data-el': 'EL-183',
    },
      h('div', { class: 'brand', style: 'padding:0;font-size:var(--fs-lg);justify-content:center' },
        h('span', { class: 'logo' }, 'F'),
        h('span', {}, 'Fincast')),
      ...filhos));
}

export function telaConvite(dados: DadosConvite, aoEntrar: () => void): HTMLElement {
  if (dados.email === '') {
    return moldura(
      h('span', { style: 'color:hsl(var(--warning));text-align:center', html: icone.alerta(26) }),
      h('h3', { style: 'margin:0;text-align:center' }, 'Link de convite incompleto'),
      h('p', { class: 'muted', style: 'margin:0;line-height:1.55;text-align:center' },
        'Este link não traz o e-mail do convite. Peça a quem convidou para copiar o link inteiro e mandar de novo.'),
    );
  }

  const nome = h('input', { class: 'input', placeholder: 'Como você quer ser chamado', autocomplete: 'name' });
  const senha = h('input', { class: 'input', type: 'password', autocomplete: 'new-password', placeholder: 'Pelo menos 8 caracteres' });
  const senha2 = h('input', { class: 'input', type: 'password', autocomplete: 'new-password', placeholder: 'Digite de novo' });
  const recado = h('div', { class: 'aviso', style: 'display:none' });
  const botao = h('button', { class: 'btn pri', style: 'width:100%;height:38px' }, 'Criar meu acesso');

  const dizer = (m: string): void => { recado.textContent = m; recado.style.display = ''; };
  const calar = (): void => { recado.style.display = 'none'; };

  async function enviar(): Promise<void> {
    calar();
    if (senha.value.length < 8) { dizer('A senha precisa de pelo menos 8 caracteres.'); senha.focus(); return; }
    if (senha.value !== senha2.value) { dizer('As duas senhas não são iguais.'); senha2.focus(); return; }

    botao.classList.add('dis');
    botao.textContent = 'Criando…';
    try {
      const r = await cadastrarComConvite({
        email: dados.email,
        senha: senha.value,
        nome: nome.value.trim(),
        token: dados.token,
      });
      if (r.precisaConfirmarEmail) {
        /* Configuração que exige confirmação por e-mail. O vínculo com a incorporadora
           ainda NÃO foi criado — `aceitarConvite` só roda quando há sessão. Dizer isso
           com clareza evita a pessoa confirmar, entrar e achar que o convite falhou ao
           ver a lista de incorporadoras vazia. */
        botao.remove();
        dizer('Enviamos um e-mail de confirmação. Confirme e depois entre — seu acesso à incorporadora é liberado na primeira entrada.');
        return;
      }
      aoEntrar();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      /* O 500 sem corpo é o gatilho recusando: convite vencido, revogado, já usado, ou
         e-mail que não bate. A frase abaixo nomeia as quatro, porque o servidor não diz
         qual — e "erro 500" não ajuda ninguém. */
      dizer(
        /(^\{\}$|500|Internal|Database error)/i.test(msg)
          ? 'Este convite não vale mais: pode ter vencido, sido revogado ou já usado. Peça um novo a quem convidou você.'
          : msg,
      );
      botao.classList.remove('dis');
      botao.textContent = 'Criar meu acesso';
    }
  }

  botao.addEventListener('click', () => void enviar());
  for (const campo of [nome, senha, senha2]) {
    campo.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') void enviar(); });
    campo.addEventListener('input', calar);
  }
  window.setTimeout(() => nome.focus(), 0);

  return moldura(
    h('p', { class: 'desc', style: 'text-align:center;margin:-6px 0 2px' },
      'Você foi convidado para o Fincast. Escolha uma senha para entrar.'),
    recado,
    h('div', { class: 'field' },
      h('label', { class: 'lbl' }, 'E-mail do convite'),
      h('input', { class: 'input ro', value: dados.email, readonly: true, tabindex: '-1' })),
    h('div', { class: 'field' }, h('label', { class: 'lbl' }, 'Seu nome'), nome),
    h('div', { class: 'field' }, h('label', { class: 'lbl' }, 'Senha'), senha),
    h('div', { class: 'field' }, h('label', { class: 'lbl' }, 'Repita a senha'), senha2),
    botao,
    h('div', { class: 'sepln' }),
    h('p', { class: 'muted', style: 'text-align:center;font-size:var(--fs-2xs);line-height:1.5;margin:0' },
      'O convite vale para este e-mail e por tempo limitado. Se der erro, peça um novo a quem convidou você.'),
  );
}
